import { useState, useEffect, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { api } from '../../api';
import { useAuth } from '../../context/AuthContext';

// ============================================================
// Sign Setup — everything the sign operation reads, editable without
// a deploy. Aug 14 revamp: crews (multi-crew ready), request-form
// editor (work types + per-type rules), supplies list, routing, days
// off. Region service days removed (soft labels since the Aug 13
// scheduling revamp).
// ============================================================

const chip = (bg, color) => ({
  fontSize: 10.5, fontWeight: 700, padding: '2px 8px', borderRadius: 999,
  background: bg, color, textTransform: 'uppercase', letterSpacing: '0.03em', whiteSpace: 'nowrap',
});

// Tile — a self-contained workspace panel. Colored rail on top gives
// each panel an identity at a glance (same treatment as the schedule
// board's proposal panel), so the page scans instead of scrolls.
function Section({ icon, title, desc, children, aside, rail = 'var(--brand-accent, #3b82f6)' }) {
  return (
    <div className="card" style={{
      padding: 0, overflow: 'hidden', display: 'flex', flexDirection: 'column',
      boxShadow: '0 1px 3px rgba(15, 23, 42, 0.06), 0 6px 20px rgba(15, 23, 42, 0.04)',
    }}>
      <div style={{ height: 4, background: rail }} />
      <div style={{
        display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 12,
        padding: '16px 20px 12px',
      }}>
        <div style={{ display: 'flex', gap: 12, alignItems: 'flex-start' }}>
          <div style={{
            width: 36, height: 36, borderRadius: 10, flexShrink: 0, fontSize: 17,
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            background: 'var(--bg-subtle, #f6f8fa)', border: '1px solid var(--border-subtle, #edf2f7)',
          }}>{icon}</div>
          <div>
            <div style={{ fontWeight: 800, fontSize: 15, letterSpacing: '-0.01em', lineHeight: 1.25 }}>{title}</div>
            {desc && <div style={{ fontSize: 12.5, color: 'var(--text-muted)', marginTop: 3, lineHeight: 1.45 }}>{desc}</div>}
          </div>
        </div>
        {aside}
      </div>
      <div style={{ padding: '4px 20px 20px', flex: 1 }}>{children}</div>
    </div>
  );
}

function Kpi({ label, value, sub }) {
  return (
    <div className="card" style={{ padding: '14px 18px', flex: 1, minWidth: 150 }}>
      <div style={{ fontSize: 22, fontWeight: 800, letterSpacing: '-0.01em', lineHeight: 1.1 }}>{value}</div>
      <div style={{ fontSize: 11, fontWeight: 700, color: 'var(--text-muted)', textTransform: 'uppercase', letterSpacing: '0.05em', marginTop: 4 }}>{label}</div>
      {sub && <div style={{ fontSize: 12, color: 'var(--text-faint, #94a3b8)', marginTop: 2 }}>{sub}</div>}
    </div>
  );
}

function Toggle({ checked, onChange, label, disabled }) {
  return (
    <label style={{ display: 'inline-flex', alignItems: 'center', gap: 7, cursor: disabled ? 'default' : 'pointer', opacity: disabled ? 0.5 : 1 }}>
      <span onClick={() => !disabled && onChange(!checked)} style={{
        width: 34, height: 19, borderRadius: 999, position: 'relative', flexShrink: 0,
        background: checked ? 'var(--brand-accent, #3b82f6)' : 'var(--border-medium, #cbd5e1)',
        transition: 'background 0.15s ease',
      }}>
        <span style={{
          position: 'absolute', top: 2, left: checked ? 17 : 2, width: 15, height: 15,
          borderRadius: '50%', background: '#fff', boxShadow: '0 1px 2px rgba(15,23,42,0.3)',
          transition: 'left 0.15s ease',
        }} />
      </span>
      <span style={{ fontSize: 12.5, fontWeight: 600, color: 'var(--text-secondary, #334155)' }}>{label}</span>
    </label>
  );
}

const inputStyle = { padding: '9px 12px', borderRadius: 10 };
// Labeled field — label above the control, the way real settings pages read.
function Field({ label, children, width }) {
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 5, width, minWidth: 0 }}>
      <span style={{ fontSize: 11, fontWeight: 700, color: 'var(--text-faint, #94a3b8)', textTransform: 'uppercase', letterSpacing: '0.05em' }}>{label}</span>
      {children}
    </div>
  );
}
const rowCard = {
  padding: '14px 16px', borderRadius: 12,
  border: '1px solid var(--border-subtle, #edf2f7)', background: 'var(--bg-card, #fff)',
  boxShadow: '0 1px 2px rgba(15, 23, 42, 0.04)',
};
const removeBtn = { fontSize: 12, color: '#b91c1c', padding: '6px 10px', fontWeight: 600 };
const addBtn = { marginTop: 12, fontSize: 13, fontWeight: 600, padding: '9px 14px' };
const fixedField = {
  display: 'flex', alignItems: 'center', gap: 8, padding: '7px 12px', borderRadius: 8,
  background: 'var(--bg-inset, #edf0f4)', fontSize: 12.5, fontWeight: 600,
  color: 'var(--text-muted, #64748b)',
};

export default function SignSetupPage() {
  const [data, setData] = useState(null);
  const [error, setError] = useState('');
  const [notice, setNotice] = useState('');
  const [busy, setBusy] = useState(false);
  const [dirty, setDirty] = useState(false);
  const [newBlackout, setNewBlackout] = useState({ date: '', reason: '' });
  const [geo, setGeo] = useState(null);
  const [geoBusy, setGeoBusy] = useState(false);

  const load = useCallback(() => {
    api.getSignSetup().then(res => { setData(res); setDirty(false); }).catch(e => setError(e.message));
    api.getSignGeocodeStatus().then(setGeo).catch(() => { });
  }, []);
  useEffect(() => { load(); }, [load]);

  if (error && !data) return <div className="admin-page"><p className="alert alert-error">{error}</p></div>;
  if (!data) return <div className="admin-page"><p>Loading…</p></div>;

  const mutate = (patch) => { setData(d => ({ ...d, ...patch })); setDirty(true); };
  const patchRow = (key, idx, rowPatch) => {
    const next = [...data[key]];
    next[idx] = { ...next[idx], ...rowPatch };
    mutate({ [key]: next });
  };

  async function save() {
    setBusy(true); setError(''); setNotice('');
    try {
      await api.saveSignSetup({
        work_types: data.work_types,
        crews: data.crews,
        inventory: data.inventory,
        shop_start_address: data.shop_start_address,
      });
      setNotice('Saved — changes apply immediately across the sign module.');
      load();
    } catch (e) {
      setError(e.message);
    } finally {
      setBusy(false);
    }
  }

  const activeCapacity = data.crews.filter(c => c.is_active !== 0).reduce((s, c) => s + (c.daily_minutes || 0), 0);

  return (
    <div className="admin-page" style={{ maxWidth: 1240, paddingBottom: 90 }}>
      <div className="page-header" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', flexWrap: 'wrap', gap: 10 }}>
        <div>
          <h2 style={{ letterSpacing: '-0.01em' }}>Sign Setup</h2>
          <p style={{ margin: '4px 0 0', fontSize: 13, color: 'var(--text-muted)' }}>
            Crews, the request form, supplies, routing, and days off — live the moment you save.
          </p>
        </div>
        <Link to="/sales/requests/sign_request/schedule" className="btn btn-muted" style={{ fontWeight: 600, fontSize: 13 }}>← Schedule Board</Link>
      </div>

      {error && <p className="alert alert-error">{error}</p>}
      {notice && <p className="alert" style={{ background: '#dcfce7', color: '#166534' }}>{notice}</p>}

      {/* ── At-a-glance ── */}
      <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap', marginBottom: 18 }}>
        <Kpi label="Active crews" value={data.crews.filter(c => c.is_active !== 0).length}
          sub={`${data.crews.length} configured`} />
        <Kpi label="Daily capacity" value={`${Math.round(activeCapacity / 60 * 10) / 10}h`}
          sub="bookable work per day" />
        <Kpi label="Form options" value={data.work_types.filter(w => w.is_active !== 0).length}
          sub={`${data.work_types.filter(w => w.is_active !== 0 && w.requires_photos).length} require photos`} />
        <Kpi label="Properties pinned" value={geo ? `${geo.geocoded ?? 0}/${geo.total ?? 0}` : '—'}
          sub={geo && geo.failed > 0 ? `${geo.failed} failed` : 'ready for Build Day'} />
      </div>

      <div className="grid-stack-mobile" style={{
        display: 'grid', gridTemplateColumns: 'minmax(0, 1.35fr) minmax(320px, 1fr)', gap: 18, alignItems: 'start',
      }}>
      {/* ── LEFT: the two heavy editors ── */}
      <div style={{ display: 'flex', flexDirection: 'column', gap: 18 }}>
      {/* ── Crews & capacity ── */}
      <Section icon="👷" title="Crews & Daily Capacity" rail="linear-gradient(90deg, #d97706, #f59e0b)"
        desc="Each crew is a truck + technician's worth of daily work. Active crews add together into the day's bookable capacity."
>
        <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
          {data.crews.map((c, idx) => (
            <div key={c.id ?? `new-${idx}`} style={{ ...rowCard, opacity: c.is_active === 0 ? 0.55 : 1 }}>
              <div style={{ display: 'flex', alignItems: 'flex-end', gap: 14, flexWrap: 'wrap' }}>
                <Field label="Crew" width={200}>
                  <input className="form-input" style={{ ...inputStyle, fontWeight: 700 }} value={c.name}
                    placeholder="Crew name"
                    onChange={e => patchRow('crews', idx, { name: e.target.value })} />
                </Field>
                <Field label="Daily capacity" width={150}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                    <input className="form-input" type="number" min="60" max="1440" step="15"
                      style={{ ...inputStyle, width: 84 }} value={c.daily_minutes}
                      onChange={e => patchRow('crews', idx, { daily_minutes: Number(e.target.value) })} />
                    <span style={{ fontSize: 12.5, color: 'var(--text-muted)', whiteSpace: 'nowrap' }}>
                      min · {((c.daily_minutes || 0) / 60).toFixed(1)}h
                    </span>
                  </div>
                </Field>
                <Field label="Default technician" width={220}>
                  <select className="form-input" style={inputStyle}
                    value={c.default_technician_user_id || ''}
                    onChange={e => patchRow('crews', idx, { default_technician_user_id: e.target.value ? Number(e.target.value) : null })}>
                    <option value="">Unassigned</option>
                    {data.techs.map(t => <option key={t.id} value={t.id}>{t.full_name}</option>)}
                  </select>
                </Field>
                <span style={{ flex: 1 }} />
                <div style={{ paddingBottom: 9 }}>
                  <Toggle checked={c.is_active !== 0} label={c.is_active !== 0 ? 'Active' : 'Inactive'}
                    onChange={v => patchRow('crews', idx, { is_active: v ? 1 : 0 })} />
                </div>
              </div>
            </div>
          ))}
        </div>
        <button className="btn btn-muted" style={addBtn}
          onClick={() => mutate({ crews: [...data.crews, { name: `Sign Crew ${data.crews.length + 1}`, daily_minutes: 420, is_active: 1, default_technician_user_id: null }] })}>
          + Add crew
        </button>
        <div style={{ fontSize: 12, color: 'var(--text-muted)', marginTop: 8 }}>
          The default technician pre-fills the Schedule Board's technician picker. With two or more
          active crews the Schedule Board shows a tab per crew — each with its own jobs, route, map,
          technician and publish.
        </div>
      </Section>

      {/* ── Request form editor ── */}
      <Section icon="📝" title="Sign Request Form" rail="linear-gradient(90deg, #2563eb, #60a5fa)"
        desc="What reps see when they submit sign work. Built-in fields are fixed; the Work Requested options and their rules are yours to edit.">
        <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap', marginBottom: 14 }}>
          {['Property', 'Region (auto)', 'Work description', 'Time estimate', 'Photos', 'Access instructions', 'Emergency flag'].map(f => (
            <span key={f} style={fixedField}>🔒 {f}</span>
          ))}
        </div>
        <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
          {data.work_types.map((wt, idx) => wt.is_active === 0 ? null : (
            <div key={wt.id ?? `new-${idx}`} style={{ ...rowCard, display: 'flex', alignItems: 'center', gap: 14, flexWrap: 'wrap' }}>
              <div style={{ display: 'flex', flexDirection: 'column', gap: 2, flexShrink: 0 }}>
                <button className="btn btn-muted" style={{ padding: '2px 8px', fontSize: 10, lineHeight: '14px' }}
                  disabled={idx === 0} title="Move up"
                  onClick={() => {
                    const next = [...data.work_types];
                    [next[idx - 1], next[idx]] = [next[idx], next[idx - 1]];
                    mutate({ work_types: next.map((w, i) => ({ ...w, sort_order: (i + 1) * 10 })) });
                  }}>▲</button>
                <button className="btn btn-muted" style={{ padding: '2px 8px', fontSize: 10, lineHeight: '14px' }}
                  disabled={idx === data.work_types.length - 1} title="Move down"
                  onClick={() => {
                    const next = [...data.work_types];
                    [next[idx], next[idx + 1]] = [next[idx + 1], next[idx]];
                    mutate({ work_types: next.map((w, i) => ({ ...w, sort_order: (i + 1) * 10 })) });
                  }}>▼</button>
              </div>
              <input className="form-input" style={{ ...inputStyle, flex: 1, minWidth: 220, fontWeight: 600 }}
                value={wt.name} placeholder="Option label — what the rep sees"
                onChange={e => patchRow('work_types', idx, { name: e.target.value })} />
              <div style={{ display: 'flex', gap: 18, alignItems: 'center', flexWrap: 'wrap' }}>
                <Toggle checked={!!wt.requires_description} label="Description required"
                  onChange={v => patchRow('work_types', idx, { requires_description: v ? 1 : 0 })} />
                <Toggle checked={!!wt.requires_photos} label="Photos required"
                  onChange={v => patchRow('work_types', idx, { requires_photos: v ? 1 : 0 })} />
              </div>
              <button className="btn btn-muted" style={removeBtn}
                title="Remove this option from the form (old requests keep their history)"
                onClick={() => {
                  if (wt.id && !window.confirm(`Remove "${wt.name}" from the form? Existing requests keep it in their history.`)) return;
                  mutate({ work_types: wt.id
                    ? data.work_types.map((w, i) => i === idx ? { ...w, is_active: 0 } : w)
                    : data.work_types.filter((_, i) => i !== idx) });
                }}>Remove</button>
            </div>
          ))}
        </div>
        <button className="btn btn-muted" style={addBtn}
          onClick={() => mutate({ work_types: [...data.work_types, { name: '', requires_description: 0, requires_photos: 0, is_active: 1, sort_order: (data.work_types.length + 1) * 10 }] })}>
          + Add work type
        </button>
        <div style={{ fontSize: 12, color: 'var(--text-muted)', marginTop: 8 }}>
          "Photos required" blocks submission until the rep attaches at least one photo.
          Removed options disappear from the form on save but keep their history on old requests.
        </div>
      </Section>
      </div>

      {/* ── RIGHT: routing, supplies, days off ── */}
      <div style={{ display: 'flex', flexDirection: 'column', gap: 18 }}>

      {/* ── Routing ── */}
      <Section icon="🗺" title="Routing & Map" rail="linear-gradient(90deg, #7c3aed, #a78bfa)"
        desc="Where the truck starts its day, and the map pins Build Day plans routes with.">
        <label style={{ fontSize: 11, fontWeight: 700, color: 'var(--text-faint)', textTransform: 'uppercase', letterSpacing: '0.05em', display: 'block', marginBottom: 6 }}>
          Route start address (shop)
        </label>
        <input className="form-input" style={{ ...inputStyle, width: '100%' }}
          value={data.shop_start_address} placeholder="Where the sign truck starts and ends its day"
          onChange={e => mutate({ shop_start_address: e.target.value })} />
        <div style={{
          marginTop: 14, display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap',
          padding: '10px 12px', borderRadius: 10, background: 'var(--bg-subtle, #f6f8fa)',
        }}>
          {geo ? (
            <>
              <span style={{ fontSize: 12.5, color: 'var(--text-secondary)', flex: 1, minWidth: 120 }}>
                📍 <strong>{geo.geocoded ?? 0}</strong> of {geo.total ?? 0} pinned
                {geo.failed > 0 && <span style={{ color: '#b91c1c' }}> · {geo.failed} failed</span>}
                {geo.missing_address > 0 && <span> · {geo.missing_address} no address</span>}
              </span>
              <button className="btn btn-muted" style={{ fontSize: 12, padding: '6px 10px' }} disabled={geoBusy}
                onClick={async () => {
                  setGeoBusy(true); setNotice(''); setError('');
                  try {
                    const r = await api.startSignGeocodeBackfill();
                    setNotice(r.message || 'Geocoding started.');
                  } catch (e) { setError(e.message); }
                  finally { setGeoBusy(false); }
                }}>Pin missing now</button>
              <button className="btn btn-muted" style={{ fontSize: 12, padding: '6px 10px' }} disabled={geoBusy}
                onClick={() => api.getSignGeocodeStatus().then(setGeo).catch(() => { })}>↻ Refresh</button>
            </>
          ) : <span style={{ fontSize: 13, color: 'var(--text-muted)' }}>Loading pin status…</span>}
        </div>
        {geo && (geo.failed > 0 || geo.missing_address > 0) && (
          <FailedPins onChange={() => api.getSignGeocodeStatus().then(setGeo).catch(() => { })} />
        )}
      </Section>

      {/* ── Supplies list ── */}
      <Section icon="🧰" title="Supplies List" rail="linear-gradient(90deg, #059669, #34d399)"
        desc="The minimum the truck carries every route day. Publishing a route freezes a copy for the technician's load list.">
        <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
          {data.inventory.map((it, idx) => (
            it.is_active === 0 ? null :
            <div key={it.id ?? `new-${idx}`} style={{ ...rowCard, display: 'flex', alignItems: 'flex-end', gap: 14, flexWrap: 'wrap' }}>
              <Field label="Item" width={260}>
                <input className="form-input" style={inputStyle} value={it.name} placeholder="Item name"
                  onChange={e => patchRow('inventory', idx, { name: e.target.value })} />
              </Field>
              <Field label="Min on truck" width={100}>
                <input className="form-input" type="number" min="0" style={inputStyle} value={it.min_daily_load}
                  onChange={e => patchRow('inventory', idx, { min_daily_load: Number(e.target.value) })} />
              </Field>
              <Field label="Unit" width={110}>
                <input className="form-input" style={inputStyle} value={it.unit} placeholder="each"
                  onChange={e => patchRow('inventory', idx, { unit: e.target.value })} />
              </Field>
              <span style={{ flex: 1 }} />
              <button className="btn btn-muted" style={{ ...removeBtn, marginBottom: 1 }}
                onClick={() => mutate({ inventory: it.id
                  ? data.inventory.map((w, i) => i === idx ? { ...w, is_active: 0 } : w)
                  : data.inventory.filter((_, i) => i !== idx) })}>Remove</button>
            </div>
          ))}
        </div>
        <button className="btn btn-muted" style={addBtn}
          onClick={() => mutate({ inventory: [...data.inventory, { name: '', unit: 'each', min_daily_load: 0, is_active: 1, sort_order: 99 }] })}>
          + Add item
        </button>
      </Section>

      {/* ── Days off — saves instantly, separate from the Save bar ── */}
      <Section icon="🚫" title="Days Off" rail="linear-gradient(90deg, #dc2626, #f87171)"
        desc="Close a date entirely (holiday, truck in the shop). Build Day and the capacity meter treat closed days as zero-capacity.">
        {data.overrides.length === 0 && <div style={{ fontSize: 13, color: 'var(--text-muted)', marginBottom: 8 }}>No upcoming days off.</div>}
        {data.overrides.map(o => (
          <div key={o.date} style={{ display: 'flex', alignItems: 'center', gap: 12, padding: '6px 0', borderBottom: '1px solid var(--border-subtle, #edf2f7)' }}>
            <div style={{ width: 110, fontWeight: 700, fontSize: 13.5 }}>{o.date}</div>
            <span style={chip(o.minutes === 0 ? '#fee2e2' : '#fef3c7', o.minutes === 0 ? '#991b1b' : '#92400e')}>
              {o.minutes === 0 ? 'Closed' : `${o.minutes} min`}
            </span>
            <div style={{ fontSize: 13, color: 'var(--text-muted)', flex: 1 }}>{o.reason || ''}</div>
            <button className="btn btn-muted" style={{ fontSize: 11.5, color: '#b91c1c', padding: '4px 10px' }}
              onClick={async () => { await api.removeSignBlackout(o.date); load(); }}>Remove</button>
          </div>
        ))}
        <div style={{ display: 'flex', gap: 8, marginTop: 12, flexWrap: 'wrap' }}>
          <input className="form-input" type="date" style={inputStyle}
            value={newBlackout.date} onChange={e => setNewBlackout(b => ({ ...b, date: e.target.value }))} />
          <input className="form-input" style={{ ...inputStyle, width: 220 }} placeholder="Reason (e.g. Holiday)"
            value={newBlackout.reason} onChange={e => setNewBlackout(b => ({ ...b, reason: e.target.value }))} />
          <button className="btn btn-muted" style={{ fontWeight: 600 }} disabled={!newBlackout.date}
            onClick={async () => {
              try {
                await api.addSignBlackout({ date: newBlackout.date, minutes: 0, reason: newBlackout.reason });
                setNewBlackout({ date: '', reason: '' });
                load();
              } catch (e) { setError(e.message); }
            }}>
            Close this day
          </button>
        </div>
      </Section>

      </div>
      </div>

      <div style={{ marginTop: 18 }}>
        <BulkImportCard />
      </div>

      {/* ── Floating save bar ── */}
      {dirty && (
        <div className="m-float-bar" style={{
          position: 'fixed', bottom: 22, right: 28, zIndex: 60,
          display: 'flex', alignItems: 'center', gap: 12, padding: '10px 12px 10px 18px',
          background: 'var(--brand-dark, #0b1121)', borderRadius: 14,
          boxShadow: '0 8px 28px rgba(15, 23, 42, 0.35)',
        }}>
          <span style={{ color: '#e2e8f0', fontSize: 13, fontWeight: 600 }}>Unsaved changes</span>
          <button className="btn btn-gold" style={{ fontWeight: 700, fontSize: 13 }} disabled={busy} onClick={save}>
            {busy ? 'Saving…' : 'Save'}
          </button>
        </div>
      )}
    </div>
  );
}

// ============================================================
// FailedPins — the properties both geocoders gave up on. Shows the exact
// address that was tried; the manager fixes it inline and retries with
// immediate pass/fail. Sorted so properties with open sign jobs come
// first (those are the ones blocking Build Day).
// ============================================================
function FailedPins({ onChange }) {
  const [rows, setRows] = useState(null);
  const [open, setOpen] = useState(false);
  const [edits, setEdits] = useState({});     // id → { address, city, zip }
  const [busyId, setBusyId] = useState(null);
  const [result, setResult] = useState({});   // id → { ok, message }
  const [manual, setManual] = useState({});   // id → coords string (shown after a failed retry)

  const load = useCallback(() => api.getSignGeocodeFailed().then(r => setRows(r.failed)).catch(() => setRows([])), []);
  useEffect(() => { if (open) load(); }, [open, load]);

  const edit = (r) => edits[r.id] || { address: r.address || '', city: r.city || '', zip: r.zip || '' };

  async function retry(r) {
    setBusyId(r.id); setResult(x => ({ ...x, [r.id]: null }));
    try {
      const res = await api.fixSignGeocode(r.id, edit(r));
      setResult(x => ({ ...x, [r.id]: res }));
      if (res.pinned) { setTimeout(() => { load(); onChange?.(); }, 900); }
    } catch (e) {
      setResult(x => ({ ...x, [r.id]: { ok: false, message: e.message } }));
    } finally {
      setBusyId(null);
    }
  }

  async function pinManually(r) {
    setBusyId(r.id);
    try {
      const res = await api.manualSignGeocode(r.id, { coords: manual[r.id] || '' });
      setResult(x => ({ ...x, [r.id]: res }));
      if (res.pinned) { setTimeout(() => { load(); onChange?.(); }, 900); }
    } catch (e) {
      setResult(x => ({ ...x, [r.id]: { ok: false, pinned: false, message: e.message } }));
    } finally {
      setBusyId(null);
    }
  }

  return (
    <div style={{ marginTop: 14 }}>
      <button className="btn btn-muted" style={{ fontSize: 12.5, fontWeight: 600, width: '100%', justifyContent: 'space-between', display: 'flex' }}
        onClick={() => setOpen(o => !o)}>
        <span>🛠 Fix failed pins</span>
        <span style={{ color: 'var(--text-muted)', fontWeight: 500 }}>{open ? 'Hide ▴' : 'Show ▾'}</span>
      </button>
      {open && (
        <div style={{ marginTop: 10, display: 'flex', flexDirection: 'column', gap: 8 }}>
          {rows === null && <div style={{ fontSize: 12.5, color: 'var(--text-muted)' }}>Loading…</div>}
          {rows && rows.length === 0 && <div style={{ fontSize: 12.5, color: '#065f46' }}>All pinned 🎉</div>}
          {rows && rows.length > 0 && (
            <div style={{ fontSize: 12, color: 'var(--text-muted)' }}>
              These addresses didn't match any map record. Usual fixes: typos, missing city or ZIP, suite numbers,
              or "Rd" vs "Dr". Edit and retry — a pin means Build Day can route it.
            </div>
          )}
          {rows && rows.map(r => {
            const e = edit(r);
            const res = result[r.id];
            return (
              <div key={r.id} style={{ ...rowCard, padding: '12px 14px' }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap', marginBottom: 8 }}>
                  <span style={{ fontWeight: 700, fontSize: 13.5 }}>{r.name}</span>
                  <span style={chip('#f1f5f9', '#334155')}>{r.region}</span>
                  {r.open_sign_jobs > 0 && <span style={chip('#fef3c7', '#92400e')}>{r.open_sign_jobs} open sign job{r.open_sign_jobs === 1 ? '' : 's'}</span>}
                  {r.geocode_status === 'no_address' && <span style={chip('#fee2e2', '#991b1b')}>No address on file</span>}
                </div>
                <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', alignItems: 'flex-end' }}>
                  <Field label="Street address" width={260}>
                    <input className="form-input" style={inputStyle} value={e.address} placeholder="1234 Main St"
                      onChange={ev => setEdits(x => ({ ...x, [r.id]: { ...e, address: ev.target.value } }))} />
                  </Field>
                  <Field label="City" width={140}>
                    <input className="form-input" style={inputStyle} value={e.city}
                      onChange={ev => setEdits(x => ({ ...x, [r.id]: { ...e, city: ev.target.value } }))} />
                  </Field>
                  <Field label="ZIP" width={90}>
                    <input className="form-input" style={inputStyle} value={e.zip}
                      onChange={ev => setEdits(x => ({ ...x, [r.id]: { ...e, zip: ev.target.value } }))} />
                  </Field>
                  <button className="btn btn-gold" style={{ fontSize: 12.5, fontWeight: 700, padding: '9px 14px' }}
                    disabled={busyId === r.id || !e.address.trim()} onClick={() => retry(r)}>
                    {busyId === r.id ? 'Trying…' : 'Save & retry'}
                  </button>
                </div>
                {res && (
                  <div style={{ marginTop: 8, fontSize: 12.5, fontWeight: 600, color: res.pinned ? '#065f46' : '#b91c1c' }}>
                    {res.pinned ? '✓ ' : '✗ '}{res.message}
                  </div>
                )}
                {res && !res.pinned && (
                  <div style={{
                    marginTop: 10, padding: '10px 12px', borderRadius: 10,
                    background: 'var(--bg-subtle, #f6f8fa)', border: '1px solid var(--border-subtle, #edf2f7)',
                  }}>
                    <div style={{ fontSize: 12.5, fontWeight: 700, marginBottom: 4 }}>Address is right but still won't match? Pin it by hand.</div>
                    <div style={{ fontSize: 12, color: 'var(--text-muted)', marginBottom: 8, lineHeight: 1.45 }}>
                      Open the property in <a href={`https://www.google.com/maps/search/${encodeURIComponent(`${e.address}, ${e.city} TX ${e.zip}`)}`}
                        target="_blank" rel="noreferrer" style={{ fontWeight: 700 }}>Google Maps ↗</a>,
                      right-click the entrance → click the coordinates to copy → paste here.
                    </div>
                    <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', alignItems: 'center' }}>
                      <input className="form-input" style={{ ...inputStyle, flex: 1, minWidth: 200 }}
                        placeholder="32.7815, -97.1900" value={manual[r.id] || ''}
                        onChange={ev => setManual(x => ({ ...x, [r.id]: ev.target.value }))} />
                      <button className="btn btn-muted" style={{ fontSize: 12.5, fontWeight: 700, padding: '9px 14px' }}
                        disabled={busyId === r.id || !(manual[r.id] || '').trim()} onClick={() => pinManually(r)}>
                        📍 Pin here
                      </button>
                    </div>
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}

// ============================================================
// Bulk Import (Aug 4) — super-admin only. Upload the "Sign Request
// Upload" spreadsheet → dry-run discrepancy report against live data
// (nothing written) → assign submitters to flagged rows → import.
// Imported jobs are attributed to the real rep and land in the
// Awaiting Schedule queue.
// ============================================================
function BulkImportCard() {
  const { isSuperAdmin } = useAuth();
  const [file, setFile] = useState(null);
  const [checking, setChecking] = useState(false);
  const [importing, setImporting] = useState(false);
  const [report, setReport] = useState(null);      // dry-run response
  const [result, setResult] = useState(null);      // commit response
  const [overrides, setOverrides] = useState({});  // { rowNum: user_id }
  const [users, setUsers] = useState([]);
  const [error, setError] = useState('');

  useEffect(() => {
    if (isSuperAdmin) api.getActiveUsers().then(setUsers).catch(() => {});
  }, [isSuperAdmin]);

  if (!isSuperAdmin) return null;

  const repNeedsAssign = (r) => r.errors.some(e => e.includes('assign a submitter'));
  // A row is importable if it has no errors, or its only errors are the
  // missing-rep kind and an override has been chosen (the server
  // revalidates with overrides on commit).
  const importableCount = report
    ? report.rows.filter(r =>
        r.errors.length === 0 ||
        (r.errors.every(e => e.includes('assign a submitter')) && overrides[r.row])
      ).length
    : 0;

  async function runCheck(e) {
    e?.preventDefault();
    if (!file) return;
    setChecking(true); setError(''); setResult(null);
    try {
      const fd = new FormData();
      fd.append('file', file);
      fd.append('overrides', JSON.stringify(overrides));
      setReport(await api.bulkImportSignRequests(fd, false));
    } catch (err) {
      setError(err.message || 'Check failed');
    } finally {
      setChecking(false);
    }
  }

  async function runImport() {
    if (!file || !report) return;
    if (!window.confirm(`Import ${importableCount} sign request(s)? They'll appear as each rep's own submission and land in Awaiting Schedule.`)) return;
    setImporting(true); setError('');
    try {
      const fd = new FormData();
      fd.append('file', file);
      fd.append('overrides', JSON.stringify(overrides));
      const res = await api.bulkImportSignRequests(fd, true);
      setResult(res);
      setReport(null);
    } catch (err) {
      setError(err.message || 'Import failed');
    } finally {
      setImporting(false);
    }
  }

  const badge = (bg, color, text, key) => (
    <span key={key} style={{
      display: 'inline-block', fontSize: 11, fontWeight: 700, padding: '2px 8px',
      borderRadius: 6, background: bg, color, marginRight: 6, marginTop: 3,
    }}>{text}</span>
  );

  return (
    <Section icon="📥" title="Bulk Import Sign Requests" rail="linear-gradient(90deg, #475569, #94a3b8)"
      desc="Upload the Sign Request spreadsheet. Full discrepancy check first — nothing imports until you confirm.">
      <form onSubmit={runCheck} style={{ display: 'flex', gap: 8, flexWrap: 'wrap', alignItems: 'center' }}>
        <input type="file" accept=".xlsx,.xls" className="form-input" style={{ padding: 8 }}
          onChange={e => { setFile(e.target.files[0] || null); setReport(null); setResult(null); setOverrides({}); }} />
        <button type="submit" className="btn btn-muted" disabled={!file || checking}>
          {checking ? 'Checking…' : 'Check Spreadsheet'}
        </button>
      </form>

      {error && <p className="alert alert-error" style={{ marginTop: 10 }}>{error}</p>}

      {result && (
        <div className="alert" style={{ marginTop: 12, background: '#dcfce7', color: '#166534' }}>
          ✅ Imported {result.summary.imported} sign request(s).
          {result.summary.blocked > 0 && ` ${result.summary.blocked} row(s) skipped (still blocked).`}{' '}
          <Link to="/sales/requests/sign_request/schedule" style={{ fontWeight: 700 }}>
            Open the Schedule Board →
          </Link>
        </div>
      )}

      {report && (
        <div style={{ marginTop: 14 }}>
          <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', marginBottom: 10 }}>
            {badge('#e0e7ff', '#3730a3', `${report.summary.total} rows`, 't')}
            {badge('#dcfce7', '#166534', `${report.summary.ready} ready`, 'r')}
            {report.summary.with_warnings > 0 && badge('#fef3c7', '#92400e', `${report.summary.with_warnings} with warnings`, 'w')}
            {report.summary.blocked > 0 && badge('#fee2e2', '#991b1b', `${report.summary.blocked} blocked`, 'b')}
          </div>

          <div style={{ overflowX: 'auto' }}>
            <table className="data-table" style={{ width: '100%', fontSize: 13 }}>
              <thead>
                <tr>
                  <th>Row</th><th>Property</th><th>Region</th><th>Rep</th><th>Work</th><th>Est</th><th>Status</th>
                </tr>
              </thead>
              <tbody>
                {report.rows.map(r => {
                  const needsRep = repNeedsAssign(r);
                  const okWithOverride = needsRep && overrides[r.row]
                    && r.errors.every(e => e.includes('assign a submitter'));
                  const blocked = r.errors.length > 0 && !okWithOverride;
                  return (
                    <tr key={r.row} style={blocked ? { background: '#fee2e230' } : undefined}>
                      <td style={{ whiteSpace: 'nowrap' }}>{r.row}</td>
                      <td>
                        <div style={{ fontWeight: 600 }}>{r.sheet.property || '—'}</div>
                        {r.resolved.property_match && r.resolved.property_match !== r.sheet.property && (
                          <div style={{ fontSize: 11, color: 'var(--text-muted)' }}>→ {r.resolved.property_match}</div>
                        )}
                      </td>
                      <td>{r.resolved.region || r.sheet.region || '—'}</td>
                      <td>
                        {needsRep ? (
                          <select className="form-input" style={{ padding: '4px 8px', fontSize: 12 }}
                            value={overrides[r.row] || ''}
                            onChange={e => setOverrides(o => ({ ...o, [r.row]: e.target.value ? Number(e.target.value) : undefined }))}>
                            <option value="">— Assign submitter —</option>
                            {users.map(u => (
                              <option key={u.id} value={u.id}>
                                {u.full_name}{u.position_name ? ` — ${u.position_name}` : ''}
                              </option>
                            ))}
                          </select>
                        ) : (r.resolved.submitter_name || r.sheet.rep || '—')}
                      </td>
                      <td style={{ maxWidth: 220 }}>{r.resolved.work_summary || '—'}</td>
                      <td style={{ whiteSpace: 'nowrap' }}>{r.resolved.estimated_minutes ? `${r.resolved.estimated_minutes}m` : '—'}</td>
                      <td style={{ maxWidth: 260 }}>
                        {r.errors.filter(e => !(e.includes('assign a submitter') && overrides[r.row])).map((e2, i) =>
                          badge('#fee2e2', '#991b1b', e2, `e${i}`))}
                        {r.warnings.map((w, i) => badge('#fef3c7', '#92400e', w, `w${i}`))}
                        {!blocked && r.warnings.length === 0 && badge('#dcfce7', '#166534', okWithOverride ? 'ready (assigned)' : 'ready', 'ok')}
                        {okWithOverride && r.warnings.length > 0 && badge('#dcfce7', '#166534', 'ready (assigned)', 'ok2')}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>

          <div style={{ display: 'flex', justifyContent: 'flex-end', marginTop: 12 }}>
            <button className="btn btn-gold" style={{ fontWeight: 700 }}
              disabled={importing || importableCount === 0} onClick={runImport}>
              {importing ? 'Importing…' : `Import ${importableCount} Job${importableCount === 1 ? '' : 's'}`}
            </button>
          </div>
        </div>
      )}
    </Section>
  );
}
